/**
 * Voyage Radar credit catalogue — the frontend half of the shared vocabulary.
 *
 * The operator document is the SOURCE OF TRUTH for every credit amount, and it
 * reaches the client as `apiCreditActions` on the sensor row — snapshotted onto
 * the quote at tasking time, then carried on the task's sensor. Read prices
 * from there (via `actionCredits` / `refreshCredits` below), never from a
 * literal, so an ee-admin re-price needs no deploy.
 *
 * The constants at the bottom are LAST-RESORT fallbacks for a row that predates
 * the field or carries an invalid edit — not the prices. The backend has the
 * same defaults in eetaskapi/helpers/voyageRadarCredits.js; a drift spec
 * asserts the two agree.
 *
 * One entry per upstream endpoint. `target` says which tasked shape can reach
 * an entry, so a screen can show a buyer only what their own task can spend on.
 */

/** Which tasked shape can reach an action. Absent means both. */
export type VoyageActionTarget = 'aoi' | 'poi';

export interface VoyageCreditAction {
  key: string;
  label: string;
  credits: number;
  description?: string;
  target?: VoyageActionTarget;
}

export const VOYAGE_RADAR_CREDIT_ACTIONS: VoyageCreditAction[] = [
  {
    key: 'refresh',
    label: 'Area vessel refresh',
    credits: 2,
    description: 'Latest vessel positions across your tasked area of interest.',
    target: 'aoi',
  },
  {
    // Separate from the area refresh because it is a separate endpoint
    // (/v1/vessels/nearby), and separately priceable. It carries no `target`
    // because it prices that ENDPOINT rather than one gesture, and the endpoint
    // is about to serve a second caller: the per-vessel "who is beside this
    // hull" search in plans/VOYAGE_RADAR_NEARBY_VESSEL_PLAN.md. Tagging the
    // entry 'poi' would hide, from AOI buyers, a line they are meant to spend
    // on the day that caller lands. One entry per upstream endpoint, read
    // literally — one key, however many callers.
    //
    // THAT SECOND CALLER IS NOT BUILT (plan steps 4-8: route, drawer, popup
    // action). The only thing that spends this key today is a point task's
    // tasked-target refresh, centred on the tasked footprint — nothing anywhere
    // lets a user select a hull and ask what is beside it, so do not go looking
    // for that UI. The description below already promises it; per the plan's
    // §6.0 it reaches no buyer until the migration rewrites the operator
    // document, which is deliberately the LAST step of the release.
    //
    // Equal cost to the area refresh today; nothing here assumes it stays equal.
    key: 'nearby',
    label: 'Nearby vessel refresh',
    credits: 2,
    description:
      'Latest vessels near your tasked point of interest, or near a vessel you select, by distance.',
  },
  {
    key: 'track',
    label: 'Vessel track history',
    credits: 5,
    description: "One vessel's recorded path, up to 90 days.",
  },
  {
    key: 'search',
    label: 'Worldwide vessel search',
    credits: 1,
    description: 'Find a vessel anywhere by name, MMSI, IMO or call sign.',
  },
  {
    key: 'lookup',
    label: 'Latest vessel details',
    credits: 1,
    description: "One vessel's latest position and details (destination, speed, dimensions) by MMSI.",
  },
  {
    key: 'portCalls',
    label: 'Port call history',
    credits: 1,
    description: "One vessel's recorded arrivals, departures and time at anchorage.",
  },
];

/**
 * Which catalogue key prices a refresh of this target shape — the client twin
 * of `refreshActionKey` in eetaskapi/helpers/voyageRadarCredits.js, so the cost
 * shown on a button is the one the route will actually charge.
 *
 * The POI branch returns `nearby`, and that key names the ENDPOINT rather than
 * this gesture: /v1/vessels/nearby is what a point task's refresh reaches, and
 * it is what a search around a user-picked vessel will reach once that caller
 * is built — plan steps 4-8, which have not started, so this function is the
 * only thing that asks for the key today. Choosing between the two refresh
 * endpoints by tasked shape is still this function's job; owning the key is
 * not, which is why `nearby` carries no `target` in the catalogue above while
 * `refresh` still does.
 */
export function refreshActionKey(isPoi: boolean): string {
  return isPoi ? 'nearby' : 'refresh';
}

/**
 * True when this sensor was tasked on a point rather than an area.
 *
 * Resolved exactly as the server resolves it — the FIRST scene carrying a
 * footprint type wins (voyageRadar.route.js) — not "any scene is a Point".
 * The two must pick the same scene or the client could price and debit a
 * nearby refresh while the server charged for a bbox one.
 */
export function isPoiSensor(sensor: any): boolean {
  const scenes = sensor?.scenes ?? [];
  const footprint = scenes.find((s: any) => s?.footprint?.type)?.footprint;
  return footprint?.type === 'Point';
}

/**
 * Credit cost of one action, from a sensor row's catalogue.
 *
 * THE way to price anything Voyage Radar on the client. A doc value always
 * wins, including one that differs from our own defaults — that difference is
 * the whole point of ops being able to re-price.
 *
 * WHEN THE FALLBACK ACTUALLY FIRES, because it is narrower than it looks and
 * the answer is not "a row with no catalogue". `normalizeCreditActions`
 * already covers that case: handed nothing valid it substitutes the SHIPPED
 * catalogue wholesale, so `.find()` hits and the `??` is never reached.
 *
 * It fires for a PARTIAL catalogue — a doc carrying some valid entries but not
 * this key, which keeps its own entries rather than inheriting the rest. That
 * is a real, live shape, not a hypothetical: a doc written before `nearby`
 * existed carries only `refresh`, and `refreshCredits` on a POI task has to
 * resolve `nearby` from the defaults rather than silently charging the area
 * price. Its spec is one screen down.
 *
 * ONE KEY, resolving both the lookup and the fallback. There used to be a third
 * `fallback` parameter defaulted to `voyageFallbackCredits(key)`. Threading a
 * constant through call sites was how a button ended up priced from one
 * action's fallback while asking for another's; the default closed that hole,
 * and the parameter WAS the hole. Every call site had already stopped passing
 * it — the last caller was one line of this function's own spec, asserting that
 * the unused parameter worked, and failing, because for a key the catalogue
 * ships it could not be reached. Removed rather than kept "in case": a caller
 * who genuinely wants a different number can write `?? n` at the call site,
 * where the reader can see it, instead of reopening a way to price the wrong
 * action.
 */
export function actionCredits(row: any, key: string): number {
  const found = normalizeCreditActions(row?.apiCreditActions).find((a) => a.key === key);
  return found?.credits ?? voyageFallbackCredits(key);
}

/**
 * Credit cost of one refresh of THIS sensor, area or point.
 *
 * Shape comes from the tasked footprint, which is also what the server reads to
 * choose its endpoint — so the two cannot disagree about which price applies.
 */
export function refreshCredits(sensor: any): number {
  // One key decides both the doc lookup AND the fallback — they cannot be
  // resolved from different shapes, which is what picking the constant by hand
  // alongside the key allowed.
  return actionCredits(sensor, refreshActionKey(isPoiSensor(sensor)));
}

/**
 * The actions a task of this shape can actually spend on.
 *
 * Filtered from the catalogue's own `target`, so an endpoint added in ee-admin
 * lands on the right screen without a client release. An entry with no `target`
 * applies to both shapes and always survives.
 */
export function actionsForTarget(
  actions: VoyageCreditAction[],
  isPoi: boolean,
): VoyageCreditAction[] {
  const wanted: VoyageActionTarget = isPoi ? 'poi' : 'aoi';
  const applicable = actions.filter((a) => !a.target || a.target === wanted);
  // Never return nothing: a catalogue whose every entry is tagged for the other
  // shape is a bad edit, and an empty price list is worse than an unfiltered
  // one — the buyer would be shown a screen that prices nothing at all.
  return applicable.length ? applicable : actions;
}

/**
 * The shipped fallback price for one action — used when a sensor row carries no
 * catalogue at all.
 *
 * Reads `VOYAGE_RADAR_CREDIT_ACTIONS` above rather than restating a number, so
 * the array is the single place a price is written on this side. Returns 0 for
 * an unknown key: an action nobody ships a price for is not free by intent, but
 * `actionCredits` only ever reaches this when the doc has no entry either, and
 * a visible 0 is a bug that shows itself rather than a plausible wrong number.
 */
export function voyageFallbackCredits(key: string): number {
  return VOYAGE_RADAR_CREDIT_ACTIONS.find((a) => a.key === key)?.credits ?? 0;
}

/**
 * Named fallbacks, DERIVED from the catalogue above — they cannot disagree with
 * it, which is what they used to do silently when both were hand-written.
 *
 * Prefer `actionCredits(row, key)`, which reads the sensor's own catalogue and
 * falls back to these automatically. The drift spec still pins the literal
 * numbers, because that guard is against the BACKEND defaults in
 * eetaskapi/helpers/voyageRadarCredits.js — the one duplication that cannot be
 * removed from here, since it lives in another codebase.
 */
export const VOYAGE_REFRESH_CREDITS = voyageFallbackCredits('refresh');
export const VOYAGE_NEARBY_CREDITS = voyageFallbackCredits('nearby');

/**
 * The radius, in km, a POI refresh covers — the operator's documented maximum.
 *
 * A POI task has no radius input by design, so this is not a preference: it is
 * the platform's fixed choice, mirrored from the backend's
 * `config.voyageRadarPoiRadiusKm` (eetaskapi/operatorApi/voyageRadarApi.js).
 *
 * Used ONLY to draw and frame the target disc before the first refresh answers.
 * Every response echoes `radiusKm`, and that value wins — so lowering the
 * server's radius takes effect without a client release.
 */
export const VOYAGE_POI_RADIUS_KM = 1000;

export const VOYAGE_TRACK_CREDITS = voyageFallbackCredits('track');
export const VOYAGE_SEARCH_CREDITS = voyageFallbackCredits('search');
export const VOYAGE_LOOKUP_CREDITS = voyageFallbackCredits('lookup');
export const VOYAGE_PORT_CALLS_CREDITS = voyageFallbackCredits('portCalls');

/**
 * Purchase bounds, as read off the operator doc.
 *
 * `max` is deliberately nullable and there are deliberately NO default
 * constants here. A hardcoded client ceiling can silently disagree with the
 * database: set the doc to 500–20000, fail to thread the fields onto one sensor
 * row, and the modal would cheerfully accept 100 while the server refuses
 * anything under 500 — the user is told one limit and rejected for another.
 * A missing ceiling means "the server decides", which is true and safe; an
 * invented one means "we guessed", which is neither.
 *
 * The floor never disappears, because it is derivable without a constant: a
 * wallet below the cheapest action in the catalogue can buy nothing.
 */
export interface VoyageCreditBounds {
  /** Smallest buyable wallet. Always a number — the catalogue guarantees it. */
  min: number;
  /** Largest buyable wallet, or null when the doc does not say. */
  max: number | null;
}

/**
 * Sanitize a catalogue read off an operator doc / sensor snapshot. Entries
 * with a blank label/key or a non-positive-integer credit cost are dropped;
 * an empty result falls back to the defaults, so a bad ee-admin edit can
 * never blank the purchase screen or zero-price an action.
 */
export function normalizeCreditActions(raw: any): VoyageCreditAction[] {
  const list = Array.isArray(raw) ? raw : [];
  const valid = list.filter(
    (a: any): a is VoyageCreditAction =>
      !!a &&
      typeof a.key === 'string' &&
      a.key.trim() !== '' &&
      typeof a.label === 'string' &&
      a.label.trim() !== '' &&
      Number.isInteger(a.credits) &&
      a.credits > 0,
  );
  return valid.length ? valid : VOYAGE_RADAR_CREDIT_ACTIONS;
}

/** Cheapest action in the catalogue — below this a wallet is stranded. */
export function minActionCredits(actions: VoyageCreditAction[]): number {
  const costs = (actions?.length ? actions : VOYAGE_RADAR_CREDIT_ACTIONS).map((a) => a.credits);
  return Math.min(...costs);
}

/**
 * Purchase bounds off a sensor row / operator doc.
 *
 * Doc values are used when they are positive whole numbers; anything else is
 * discarded rather than repaired, because a repaired bound is a bound the
 * server does not share. The floor falls back to the cheapest action — derived,
 * not hardcoded — and the ceiling falls back to null, meaning the modal imposes
 * none and the server has the final word.
 *
 * An inverted pair is a typo, not an intent: both values are dropped rather
 * than guessing which one the editor meant.
 */
export function resolveCreditBounds(row: any, actions?: VoyageCreditAction[]): VoyageCreditBounds {
  const whole = (value: any): number | null => {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  let min = whole(row?.minApiCredits);
  let max = whole(row?.maxApiCredits);

  if (min !== null && max !== null && min > max) {
    min = null;
    max = null;
  }

  // A wallet below the cheapest action can buy nothing, so this floor holds
  // whatever the document says — and needs no constant to express.
  const cheapest = minActionCredits(actions ?? VOYAGE_RADAR_CREDIT_ACTIONS);
  if (min === null || min < cheapest) min = cheapest;
  if (max !== null && max < min) min = max;

  return { min, max };
}

/**
 * The ONE affordability test. `null` means the balance is UNKNOWN, not empty —
 * an unknown balance allows the click and lets the server-side gate decide.
 * Never write `remaining < cost` inline: `null < 5` is `true` in JS, so the
 * naive form silently flips unknown from "allow" to "refuse".
 */
export function canAfford(remaining: number | null | undefined, cost: number): boolean {
  return remaining == null || remaining >= cost;
}
